import { ShoppingCartItem } from "@/components/Checkout/types";
import { useAppSelector } from "@/redux/hooks";
import { fetchProductMetadataWithGraphQL } from "@/redux/reducers/checkout/thunk";
import { useApolloClient } from "@apollo/client";
import {
  CampaignType,
  FallbackImage,
  formatMoney,
  getAppConsistentParams,
  getDefaultProductImage,
  getFallbackProductImage,
  getProductUrl,
  getPurchaseRewardAmountForProduct,
  MOBILE_SCREEN_SIZE,
  ProductRewardSearchResult,
  RewardPillWithText,
} from "@brandclub/common-ui";
import { RewardUnlockStrategy } from "@brandclub/types";
import {
  Box,
  Skeleton,
  styled,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const CartProductContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  textDecoration: "none",
  color: theme.palette.primary.main,

  gap: 20,

  ".product_image": {
    objectFit: "contain",
    width: "72px",
    height: "72px",

    [theme.breakpoints.down("md")]: {
      width: "60px",
      height: "60px",
    },
  },

  ".product_detail": {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    width: "100%",
    gap: 6,
    ".info": {
      display: "flex",
      flexDirection: "column",
      gap: 8,
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        gap: 4,
      },
      ".price": {
        display: "flex",
        alignItems: "center",
        fontSize: 16,
        fontWeight: 600,
        gap: 13,
        [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          gap: 11,
        },
      },
      ".name": {
        fontSize: 12,
        fontWeight: 500,
      },
    },
    ".actions": {
      display: "flex",
      gap: 50,
      alignItems: "center",
      ".quantity": {},
      ".remove": {
        fontSize: 14,
        fontWeight: 500,
        "&:hover": {
          cursor: "pointer",
          textDecoration: "underline",
        },
      },
    },
  },

  "&.large": {
    gap: 30,
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      gap: 25,
    },
    ".product_image": {
      width: 115,
      height: 115,
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        width: "92px",
        height: "104px",
      },
    },
    ".product_detail": {
      gap: 10,
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        gap: 6,
      },

      ".info": {
        ".price": {
          fontWeight: 600,
          fontSize: 16,
        },
        ".name": {
          fontWeight: 500,
          fontSize: 14,
        },
      },
      ".actions": {
        justifyContent: "space-between",

        ".remove": {
          fontWeight: 500,
          fontSize: 14,
          [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
            fontSize: 12,
          },
        },
      },
    },
  },
}));

const QuantityBadge = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  backgroundColor: "white",
  borderRadius: "50%",
  border: `1px solid ${theme.palette.primary.main}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 10,
  fontWeight: 600,
}));

export const OrderSummaryProduct = ({
  large = false,
  product,
  className,
}: {
  large?: boolean;
  product: ShoppingCartItem;
  className?: string;
}) => {
  const cartProducts = useAppSelector((state) => state.checkout.products);
  const userProfile = useAppSelector((state) => state.userProfile);
  const cartProduct = cartProducts?.[product.stacklineSku];
  const [productMetadata, setProductMetadata] =
    useState<ProductRewardSearchResult | null>(null);
  const apolloClient = useApolloClient();
  useEffect(() => {
    const fetchProductMetadata = async () => {
      const [productMetadata] = await fetchProductMetadataWithGraphQL(
        apolloClient,
        [product.stacklineSku],
        !!userProfile?.userId
      );
      // console.log("productMetadata from fetchProductMetadataWithGraphQL", productMetadata);
      setProductMetadata(productMetadata);
    };
    if (cartProduct) {
      // console.log("productMetadata from cartProduct", cartProduct);
      setProductMetadata(cartProduct);
    } else {
      fetchProductMetadata();
    }
  }, [product.stacklineSku, userProfile?.userId, cartProduct, apolloClient]);

  const metadata =
    productMetadata ??
    (product as ShoppingCartItem & ProductRewardSearchResult);

  const retailPrice = product.retailPrice || metadata?.retailPrice;
  // hasActiveInstantReward on metadata is not always set, so we need to check the campaignRewards as well
  const hasActiveInstantReward =
    !!metadata?.hasActiveInstantReward ||
    (!!metadata?.campaignRewards &&
      metadata?.campaignRewards.some(
        (campaignReward) =>
          campaignReward?.campaignType ===
            CampaignType.DailyDealsRewardCampaign ||
          (campaignReward &&
            "rewardUnlockStrategy" in campaignReward &&
            campaignReward.rewardUnlockStrategy ===
              RewardUnlockStrategy.Instant)
      ));
  const rewardAmount = metadata && getPurchaseRewardAmountForProduct(metadata);
  const rewardPercent =
    !!metadata?.retailPrice && !!rewardAmount
      ? (rewardAmount * 100) / metadata.retailPrice
      : 0;

  const isMobileView = useMediaQuery(`(max-width: ${MOBILE_SCREEN_SIZE}px)`);
  const urlSearchParams = getAppConsistentParams(window.location.search);
  const appConfig = useAppSelector(({ appConfig }) => appConfig);

  if (!metadata?.title) {
    return (
      <CartProductContainer>
        <Skeleton className="product_image" />
        <Box className="product_detail">
          <Box className="info">
            <Skeleton className="price" />
            <Skeleton className="name" />
          </Box>
        </Box>
      </CartProductContainer>
    );
  }

  return (
    <Link
      to={getProductUrl({
        title: metadata.title,
        stacklineSku: metadata.stacklineSku,
        urlSearchParams,
        dtcProductPageUrl: metadata.dtcProductPageUrl,
        storeType: appConfig?.domainConfig?.storeBrandingType,
      })}
      style={{ textDecoration: "none" }}
      target="_blank"
    >
      <CartProductContainer
        className={[large && "large", className].filter(Boolean).join(" ")}
      >
        <Box>
          <FallbackImage
            title={metadata?.title ?? product.stacklineSku}
            className="product_image"
            src={getDefaultProductImage(product?.stacklineSku || "")}
            fallbackImage={getFallbackProductImage(product?.stacklineSku || "")}
          />
          <QuantityBadge
            sx={(theme) => ({
              position: "relative",
              bottom: 17,
              left: 61,
              [theme.breakpoints.down("md")]: {
                bottom: 15,
                left: 35,
              },
            })}
          >
            {product.quantity}
          </QuantityBadge>
        </Box>
        <Box className={"product_detail"}>
          <Box className={"info"}>
            <Box className={"price"}>
              {formatMoney(retailPrice)}
              {!!rewardAmount && (
                <RewardPillWithText
                  rewardAmount={rewardAmount}
                  rewardPercentText="reward"
                  baseFontSize={isMobileView ? 12 : 14}
                  pillContainerStyle={{
                    boxSizing: "border-box",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  rewardPercent={rewardPercent}
                  rewardCampaignType={
                    hasActiveInstantReward
                      ? CampaignType.DailyDealsRewardCampaign
                      : CampaignType.PurchaseRewardCampaign
                  }
                />
              )}
            </Box>
            <Typography
              className={"name"}
              title={metadata?.title}
              sx={{
                display: "-webkit-box",
                overflow: "hidden",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
              }}
            >
              {metadata?.title ?? "Unknown Product"}
            </Typography>
          </Box>
        </Box>
      </CartProductContainer>
    </Link>
  );
};
